import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  InputAdornment,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { CleaningService, CloseSmall } from "@tiny/icons";
import EmailFastOutline from "@tiny/icons/generated/EmailFastOutline";
import { fetchApi } from "@tiny/utils";

interface ProductDetails {
  cost: number;
  quantity: number;
  productName: string;
  productDescription: string;
  cpm: number;
}

interface ProductTier {
  name: string;
  description: string;
  productId: string;
  startingCount: number;
  endingCount: number;
  cost: number;
  minCost: number;
}

interface ProductDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  productId: string;
  segmentId: string;
}

const formatNumber = (num: number) => num.toLocaleString();

const ProductDetailsDialog: React.FC<ProductDetailsDialogProps> = ({
  open,
  onClose,
  productId,
  segmentId,
}) => {
  const [productDetails, setProductDetails] = useState<ProductDetails | null>(
    null
  );
  const [tiers, setTiers] = useState<ProductTier[]>([]);
  const [isCostLoading, setIsCostLoading] = useState(false);
  const [isTiersLoading, setIsTiersLoading] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);

  const calculateCost = React.useCallback(async () => {
    setIsCostLoading(true);
    try {
      const response = await fetchApi<ProductDetails>(
        "/billingservice/product/calculate-cost",
        {
          method: "POST",
          body: JSON.stringify({ productId, segmentId }),
        }
      );
      setProductDetails(response);
    } catch (error) {
      console.error("Error calculating cost:", error);
    } finally {
      setIsCostLoading(false);
    }
  }, [productId, segmentId]);

  const fetchTiers = React.useCallback(async () => {
    setIsTiersLoading(true);
    try {
      const response = await fetchApi<ProductTier[]>(
        `/billingservice/product/${productId}/tiers`,
        {
          method: "GET",
        }
      );
      setTiers(response);
    } catch (error) {
      console.error("Error fetching tiers:", error);
    } finally {
      setIsTiersLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    if (open && segmentId && productId) {
      calculateCost();
      fetchTiers();
    }
  }, [open, segmentId, productId, calculateCost, fetchTiers]);

  const getActiveTierIndex = (quantity: number, tiersList: ProductTier[]) => {
    if (tiersList.length > 0 && quantity < tiersList[0].startingCount) {
      return -1;
    }
    const index = tiersList.findIndex((tier) => {
      const isInRange =
        quantity >= tier.startingCount &&
        (tier.endingCount === -1 || quantity <= tier.endingCount);
      return isInRange;
    });
    if (index === -1 && tiersList.length > 0) {
      const lastTier = tiersList[tiersList.length - 1];
      if (lastTier.endingCount === -1 && quantity >= lastTier.startingCount) {
        return tiersList.length - 1;
      }
    }
    return index;
  };

  const handleProceedToPayment = async () => {
    setIsPurchasing(true);
    try {
      const response = await fetchApi<{ confirmationUrl: string }>(
        "/billingservice/product/purchase/shopify",
        {
          method: "POST",
          body: JSON.stringify({ productId, segmentId }),
        }
      );
      if (window.top && response?.confirmationUrl) {
        window.top.location.href = response.confirmationUrl;
      }
    } catch (error) {
      console.error("Error during purchase:", error);
    } finally {
      setIsPurchasing(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          fontWeight: "bold",
        }}
      >
        <Typography variant="h2">
          {productDetails?.productName || "Product Details"}
        </Typography>
        <CloseSmall
          color="action"
          sx={{ cursor: "pointer" }}
          onClick={onClose}
        />
      </DialogTitle>
      <Divider sx={{ mt: 4 }} />
      <DialogContent sx={{ py: 3 }}>
        {isCostLoading && !productDetails && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={300}
          >
            <CircularProgress />
          </Box>
        )}

        {!isCostLoading && productDetails && (
          <Box>
            {/* INFO BOX */}
            <Box
              sx={{
                p: 3,
                bgcolor: "#ecfdf5",
                border: "1px solid #a7f3d0",
                borderRadius: 2,
                mb: 4,
              }}
            >
              <Typography variant="h3" color="text.primary" gutterBottom>
                How it works!
              </Typography>

              <Typography variant="p2" color="text.secondary" sx={{ mb: 2 }}>
                This paid service scans your selected audience and identifies
                inactive, low-engagement, and suspicious email contacts based on
                activity and quality checks. Contacts that fail to meet
                engagement or validity criteria will be marked as{" "}
                <strong>Inactive</strong>, while the rest will remain{" "}
                <Box
                  component="span"
                  sx={{ fontWeight: 700, color: "primary.main" }}
                >
                  Subscribed
                </Box>
                .
              </Typography>

              <Typography variant="p2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Processing time:</strong> List cleaning begins after
                payment is completed and typically finishes within{" "}
                <strong>24 hours</strong>. Larger lists may take longer
                depending on list size and overall email quality. Once the
                cleaning process is complete, you will be notified via email.
              </Typography>

              <Typography variant="p2" color="text.secondary">
                <strong>Pricing:</strong> Charges are dynamic and based on list
                size. As your audience grows, the effective{" "}
                <strong>CPM decreases</strong>, giving you better value at
                scale.
              </Typography>
            </Box>

            <Box
              display="flex"
              sx={{ gap: 4, flexDirection: { xs: "column", md: "row" } }}
            >
              {/* PRODUCT */}
              <Box flex={1}>
                <Typography
                  variant="label3"
                  color="text.secondary"
                  sx={{
                    textTransform: "uppercase",
                    display: "block",
                    mb: 1.5,
                  }}
                >
                  PRODUCT
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1.5,
                      bgcolor: "rgba(0, 128, 96, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "primary.main",
                      flexShrink: 0,
                    }}
                  >
                    <CleaningService style={{ width: 28, height: 28 }} />
                  </Box>

                  <Box flex={1}>
                    <Typography variant="p1" fontWeight="bold" sx={{ mt: 1 }}>
                      {productDetails.productName}
                    </Typography>

                    <Typography variant="p2" color="text.secondary">
                      {productDetails.productDescription}
                    </Typography>
                  </Box>
                </Box>

                <Box mt={3}>
                  <Typography
                    variant="label3"
                    color="text.secondary"
                    sx={{
                      textTransform: "uppercase",
                      display: "block",
                      mb: 1.5,
                    }}
                  >
                    NUMBER OF EMAILS
                  </Typography>

                  <TextField
                    value={formatNumber(productDetails.quantity)}
                    disabled
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <EmailFastOutline />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>

              {/* ORDER SUMMARY */}
              <Box flex={1}>
                <Typography
                  variant="label3"
                  color="text.secondary"
                  sx={{
                    textTransform: "uppercase",
                    display: "block",
                    mb: 1.5,
                  }}
                >
                  ORDER SUMMARY
                </Typography>

                <Box
                  sx={{
                    p: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box display="flex" justifyContent="space-between" mb={4}>
                    <Typography variant="p2" color="text.secondary">
                      {productDetails.productName}
                    </Typography>

                    <Typography variant="p1" fontWeight="600">
                      ${formatNumber(productDetails.cost)}
                    </Typography>
                  </Box>

                  <Box
                    pt={3}
                    sx={{ borderTop: "1px solid", borderColor: "divider" }}
                  >
                    <Typography
                      variant="label3"
                      color="text.secondary"
                      sx={{ textTransform: "uppercase" }}
                    >
                      TOTAL DUE
                    </Typography>

                    <Typography variant="h1" fontWeight="bold" sx={{ mt: 1 }}>
                      ${formatNumber(productDetails.cost)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* PRICING TIERS TABLE - Full Width */}
            {tiers && tiers.length > 0 && (
              <Box mt={4}>
                <Typography
                  variant="label3"
                  color="text.secondary"
                  sx={{
                    textTransform: "uppercase",
                    display: "block",
                    mb: 1.5,
                  }}
                >
                  PRICING TIERS
                </Typography>

                <TableContainer
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "grey.50" }}>
                        <TableCell>
                          <Typography variant="label3" fontWeight="bold">
                            Tier
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="label3" fontWeight="bold">
                            Emails Cleaned
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="label3" fontWeight="bold">
                            CPM (per 1,000)
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="label3" fontWeight="bold">
                            Minimum Charge
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tiers.map((tier, index) => {
                        const isActive =
                          getActiveTierIndex(productDetails.quantity, tiers) ===
                          index;
                        const emailRange =
                          tier.endingCount === -1
                            ? `${formatNumber(tier.startingCount)} – ∞`
                            : `${formatNumber(
                                tier.startingCount
                              )} – ${formatNumber(tier.endingCount)}`;

                        return (
                          <TableRow
                            key={index}
                            sx={{
                              bgcolor: isActive
                                ? "rgba(0, 128, 96, 0.08)"
                                : "transparent",
                              borderLeft: isActive
                                ? "3px solid"
                                : "3px solid transparent",
                              borderLeftColor: isActive
                                ? "primary.main"
                                : "transparent",
                            }}
                          >
                            <TableCell>
                              <Typography
                                variant="p2"
                                fontWeight={isActive ? 700 : 400}
                              >
                                {index + 1}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="p2"
                                fontWeight={isActive ? 700 : 400}
                              >
                                {emailRange}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="p2"
                                fontWeight={isActive ? 700 : 400}
                              >
                                ${(tier.cost * 1000).toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="p2"
                                fontWeight={isActive ? 700 : 400}
                              >
                                ${tier.minCost.toFixed(2)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}

        {!isCostLoading && !productDetails && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={300}
          >
            <Typography variant="p1" color="text.secondary">
              Unable to load product details
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          justifyContent: "space-between",
          flexDirection: { xs: "column-reverse", sm: "row-reverse" },
          gap: 2,
          mt: 12,
        }}
      >
        <Button
          onClick={handleProceedToPayment}
          variant="contained"
          disabled={!productDetails || isCostLoading || isPurchasing}
          sx={{
            py: 1.5,
            px: 4,
            fontWeight: 700,
            minWidth: { xs: "100%", sm: 280 },
          }}
        >
          {isPurchasing ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Proceed to Payment"
          )}
        </Button>

        <Button
          onClick={onClose}
          variant="text"
          disabled={isPurchasing}
          sx={{
            fontWeight: 600,
            py: 1.5,
            px: 3,
            minWidth: { xs: "100%", sm: "auto" },
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductDetailsDialog;
